import type { TPersona } from 'constants/navigation';

const PERMISSIONS = [
  'home:view',
  'pipeline:overview',
  'applicants:view-new',
  'applicants:view-own',
  'applicants:view-all',
  'applicants:view-archived',
  'applicants:advance',
  'applicants:open-seat',
  'applicants:reject',
  'applicants:archive',
  'applicants:reassign',
  'interviews:list-own',
  'interviews:list-all',
  'interviews:calendar-own',
  'interviews:calendar-all',
  'background-check:view',
  'background-check:view-failed',
  'further-review:view',
  'final-approval:decide',
  'boss-applicants:manage',
  'verified:view',
  'approved:view',
  'waitlist:manage',
  'capacity:view',
  'activation:view',
  'on-the-app:view',
  'invitations:manage',
  'clients:view-own',
  'clients:view-all',
  'singles:view-own',
  'singles:view-all',
  'singles:search',
  'recs:create',
  'recs:view-own',
  'recs:view-all',
  'messages:view',
  'billing:desk',
  'billing:review',
  'billing:view-all',
  'members:view',
  'members:manage',
  'members:reassign',
  'members:change-type',
  'admin:concierges',
  'admin:manage',
  'admin:notifications',
  'admin:invitation-policy',
  'admin:safety-reports',
  'admin:audit-log',
] as const;

type TPermission = typeof PERMISSIONS[number];

const PIPELINE_INTAKE = [
  'applicants:view-new',
  'verified:view',
  'approved:view',
  'on-the-app:view',
] as const;

const PIPELINE_REVIEW = [
  'background-check:view',
  'background-check:view-failed',
  'further-review:view',
] as const;

/**
 * Reaches My Applicants. An applicant is owned by exactly one Recruiter, assigned by
 * round-robin at APPLIED (TRD FR-077), so for a Recruiter the screen is scoped to that
 * ownership. Every other holder sees the whole pipeline there: the design draws both
 * variants of each pipeline screen, `u_dana` scoped and `u_marcus (View all)` not, and
 * the screen picks between them on `applicants:view-all`. The Concierge holds this
 * because it works applicants too, even though its own book — members, filled by a
 * separate round-robin at activation (FR-081) — is a different thing entirely.
 */
const OWN_RECRUITS = ['applicants:view-own'] as const;

/** TRD FR-063(e) — My Interviews, list and calendar. Held by Recruiter and Concierge alike. */
const OWN_INTERVIEWS = ['interviews:list-own', 'interviews:calendar-own'] as const;

const CONCIERGE_DESK = [
  'members:view',
  'clients:view-own',
  'clients:view-all',
  'singles:view-own',
  'singles:view-all',
  'singles:search',
  'recs:create',
  'recs:view-own',
  'recs:view-all',
  'messages:view',
  'billing:desk',
] as const;

const ADMIN_CONSOLE = [
  'admin:concierges',
  'admin:manage',
  'admin:notifications',
  'admin:invitation-policy',
  'admin:safety-reports',
  'admin:audit-log',
] as const;

/** TRD §3 — Recruiter: owns round-robin-assigned applicants, schedules interviews, scores. */
const RECRUITER_ROLE = [
  'home:view',
  ...PIPELINE_INTAKE,
  ...OWN_RECRUITS,
  ...OWN_INTERVIEWS,
  'applicants:advance',
  'applicants:reject',
  'applicants:archive',
  'invitations:manage',
] as const;

/**
 * TRD FR-063 lists six applicant sub-views for the Concierge: (a) Applicants Pool,
 * (b) All Applicants, (c) Background Check, (d) Further Review, (e) My Interviews,
 * (f) All Interviews. My Applicants is a seventh, held here because the design draws it
 * for a Concierge in its view-all variant. There is still no claim action, at any stage.
 */
const CONCIERGE_ROLE = [
  'home:view',
  ...PIPELINE_INTAKE,
  ...PIPELINE_REVIEW,
  ...OWN_RECRUITS,
  'applicants:view-all',
  ...OWN_INTERVIEWS,
  'interviews:list-all',
  'applicants:advance',
  'applicants:reject',
  'applicants:archive',
  'invitations:manage',
  ...CONCIERGE_DESK,
] as const;

/**
 * TRD FR-079 — the Recruiter-Manager is a permission flag on a Recruiter, adding nine
 * permissions to the base role rather than replacing it. One of the nine has no screen
 * yet and therefore no permission here: applicants.attribution_correct.
 * oversight.second_approval was absorbed by the
 * Super-Admin final-approval gate (FR-080), and calibration_audits.view has no surface.
 */
const RECRUITER_MANAGER_ROLE = [
  ...RECRUITER_ROLE,
  ...PIPELINE_REVIEW,
  'pipeline:overview',
  'applicants:view-all',
  'applicants:reassign',
  'interviews:list-all',
] as const;

/**
 * TRD FR-081 — a flag on a Concierge. It adds reassignment of a member's owning Concierge
 * and the persona override on the member record; the second approval still has no screen.
 */
const CONCIERGE_MANAGER_ROLE = [
  ...CONCIERGE_ROLE,
  'members:reassign',
  'members:change-type',
] as const;

const GRANTS = {
  'recruiter': RECRUITER_ROLE,
  'recruiter_manager': RECRUITER_MANAGER_ROLE,
  'concierge': CONCIERGE_ROLE,
  'concierge_manager': CONCIERGE_MANAGER_ROLE,
  'super_admin': [
    'home:view',
    ...PIPELINE_INTAKE,
    ...PIPELINE_REVIEW,
    ...ADMIN_CONSOLE,
    'pipeline:overview',
    'applicants:view-all',
    'applicants:view-archived',
    'applicants:advance',
    'applicants:open-seat',
    'applicants:reject',
    'applicants:archive',
    'applicants:reassign',
    'interviews:list-all',
    'interviews:calendar-all',
    'final-approval:decide',
    'boss-applicants:manage',
    'waitlist:manage',
    'capacity:view',
    'activation:view',
    'invitations:manage',
    'clients:view-all',
    'singles:view-all',
    'members:view',
    'members:manage',
    'members:reassign',
    'members:change-type',
    'billing:review',
    'billing:view-all',
  ],
} as const satisfies Record<TPersona, readonly TPermission[]>;

const GRANT_SETS: Record<TPersona, ReadonlySet<TPermission>> = {
  'recruiter': new Set(GRANTS.recruiter),
  'recruiter_manager': new Set(GRANTS.recruiter_manager),
  'concierge': new Set(GRANTS.concierge),
  'concierge_manager': new Set(GRANTS.concierge_manager),
  'super_admin': new Set(GRANTS.super_admin),
};

export type {
  TPermission,
};
export {
  GRANTS,
  GRANT_SETS,
  PERMISSIONS,
};
